<template>
  <NListboxOption
    :value="value"
    :label="watchlist.title"
    @mouseenter="setHovered(true)"
    @mouseleave="setHovered(false)"
  >
    <div class="w-full flex justify-between items-center">
      <NText weight="medium" variant="caption1">
        {{ watchlist.title }}
      </NText>

      <div v-if="hovered" class="flex">
        <NegarTrashLightIcon
          ref="deleteWatchlistButtonRef"
          v-gid="{
            module: 'watchlist',
            screen: 'home',
            section: 'watchlistDropdown',
            component: 'deleteWatchlistButton',
          }"
          size="20"
          class="cursor-pointer ml-2"
          @click.stop="openDeleteDialog"
        />

        <NegarPenIcon
          size="20"
          class="cursor-pointer"
          @click.stop="openEditDialog"
        />
      </div>
    </div>
  </NListboxOption>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDialog, useToast } from '@negar/core';
import { NegarTrashLightIcon, NegarPenIcon } from '@negar/icons';
import { useToggle } from '@libs/use';
import { Watchlist } from '@libs/api-reference';
import { useQueryClient } from '@libs/use';
import {
  useDeleteWatchlist,
  useUpdateWatchlistTitle,
  WATCHLIST_QUERY_KEYS,
} from '@core/watchlist';

import DeleteWatchlistDialog from './DeleteWatchlistDialog.vue';
import EditWatchlistDialog from './EditWatchlistDialog.vue';

type WatchlistItemEmitsType = {
  (e: 'afterDelete', deletedValue: number): void;
  (e: 'afterUpdate', updatedValue: number): void;
  (e: 'close'): void;
};

type WatchlistItemPropsType = {
  watchlist: Watchlist;
  value: number;
};

const emit = defineEmits<WatchlistItemEmitsType>();

const props = defineProps<WatchlistItemPropsType>();

const deleteWatchlistButtonRef = ref<HTMLElement>();

const { active: hovered, toggle: setHovered } = useToggle();

const { t } = useI18n();

const toast = useToast();
const queryClient = useQueryClient();

const { add: addDialog, remove: removeDialog } = useDialog();
const { deleteWatchlist } = useDeleteWatchlist({
  onSuccess: () => {
    emit('afterDelete', props.value);

    removeDialog();

    toast.neutral({
      message: t('modules.watchlist.delete_watchlist_message', {
        watchlistName: props.watchlist.title,
      }),
      duration: 3000,
    });
  },
  onReject: () => {
    toast.negative({
      message: t('modules.watchlist.delete_watchlist_failed_message', {
        watchlistName: props.watchlist.title,
      }),
      duration: 3000,
    });
  },
});

const { updateWatchlistTitle } = useUpdateWatchlistTitle({
  onSuccess: () => {
    emit('afterUpdate', props.value);

    removeDialog();

    toast.neutral({
      message: t('modules.watchlist.edit_watchlist_message', {
        watchlistName: props.watchlist.title,
      }),
      duration: 3000,
    });
  },
  onReject: () => {
    toast.negative({
      message: t('modules.watchlist.edit_watchlist_failed_message', {
        watchlistName: props.watchlist.title,
      }),
      duration: 3000,
    });
  },
});

const openDeleteDialog = () => {
  addDialog({
    title: t('modules.watchlist.delete_watchlist_title', {
      watchlistName: props.watchlist.title,
    }),
    bodyClass: '!w-1/5',
    referenceElementRef: deleteWatchlistButtonRef,
    placement: 'top',
    content: computed(() => ({
      component: DeleteWatchlistDialog,
      props: {
        watchlist: props.watchlist,
        onDelete: deleteWatchlist,
      },
    })),
  });
};

const openEditDialog = () => {
  emit('close');

  addDialog({
    title: t('modules.watchlist.edit_watchlist_title', {
      watchlistName: props.watchlist.title,
    }),
    bodyClass: '!w-1/5',
    content: computed(() => ({
      component: EditWatchlistDialog,
      props: {
        watchlist: props.watchlist,
        onEdit: updateWatchlistTitle,
      },
    })),
  });
};
</script>
